import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Button, Badge, Modal, Form } from 'react-bootstrap';
import { stickerPacksApi } from '../../api/stickerPacks';
import { filesApi } from '../../api/files';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import './AdminStickers.css';

/**
 * Административная страница управления стикерпаками и стикерами.
 * Позволяет просматривать, создавать, редактировать и удалять паки,
 * а также добавлять/удалять стикеры внутри паков.
 */
const AdminStickers = () => {
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Пагинация
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    // Модалка создания пака
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newPackName, setNewPackName] = useState('');
    const [newPackFiles, setNewPackFiles] = useState([]);
    const [newPackEmojis, setNewPackEmojis] = useState('');
    const [createProgress, setCreateProgress] = useState(null);

    // Модалка просмотра/редактирования пака
    const [showPackModal, setShowPackModal] = useState(false);
    const [selectedPack, setSelectedPack] = useState(null);
    const [packStickers, setPackStickers] = useState([]);
    const [packLoading, setPackLoading] = useState(false);

    // Добавление стикеров в существующий пак
    const [addStickerFiles, setAddStickerFiles] = useState([]);
    const [addStickerUploading, setAddStickerUploading] = useState(false);

    // Модалка редактирования названия пака
    const [showEditModal, setShowEditModal] = useState(false);
    const [editPackName, setEditPackName] = useState('');

    // Модалка удаления
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // ====================================================================
    //  Загрузка паков
    // ====================================================================

    const loadPacks = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await stickerPacksApi.getStickerPacks({ page: currentPage - 1, size: pageSize });
            const list = Array.isArray(data) ? data : (data?.content || []);
            const total = Array.isArray(data) ? data.length : (data?.totalElements || data?.total || list.length);
            setPacks(list);
            setTotalItems(total);
        } catch (err) {
            console.error('[AdminStickers] Failed to load packs:', err);
            setError('Не удалось загрузить стикерпаки');
            setPacks([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize]);

    useEffect(() => {
        loadPacks();
    }, [loadPacks]);

    // ====================================================================
    //  Создание пака
    // ====================================================================

    const handleOpenCreate = () => {
        setNewPackName('');
        setNewPackFiles([]);
        setNewPackEmojis('');
        setCreateProgress(null);
        setShowCreateModal(true);
    };

    const handleCreatePack = async () => {
        if (!newPackName.trim()) {
            setError('Введите название пака');
            return;
        }
        if (newPackFiles.length === 0) {
            setError('Выберите хотя бы один файл стикера');
            return;
        }

        try {
            setActionLoading(true);
            setCreateProgress({ step: 'upload-stickers', completed: 0, total: newPackFiles.length + 1 });

            const emojiList = newPackEmojis
                ? newPackEmojis.split(',').map(e => e.trim()).filter(Boolean)
                : [];

            await stickerPacksApi.createPackFromFiles({
                name: newPackName.trim(),
                stickerFiles: newPackFiles,
                emojis: emojiList,
                onProgress: (progress) => setCreateProgress({ ...progress }),
            });

            setShowCreateModal(false);
            setNewPackName('');
            setNewPackFiles([]);
            setNewPackEmojis('');
            setCreateProgress(null);
            await loadPacks();
        } catch (err) {
            setError(`Не удалось создать пак: ${err.message}`);
            console.error('[AdminStickers] Create pack failed:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // ====================================================================
    //  Просмотр пака (загрузка стикеров)
    // ====================================================================

    const handleOpenPack = async (pack) => {
        setSelectedPack(pack);
        setShowPackModal(true);
        setPackLoading(true);
        setAddStickerFiles([]);
        setAddStickerUploading(false);

        try {
            // Пытаемся получить стикеры из пака, если они есть в объекте
            if (pack.stickers && pack.stickers.length > 0) {
                setPackStickers(pack.stickers);
            } else {
                const stickers = await stickerPacksApi.getStickerPackStickers(pack.id);
                setPackStickers(stickers);
                // Обновляем пак в списке
                setPacks(prev => prev.map(p =>
                    p.id === pack.id ? { ...p, stickers } : p
                ));
            }
        } catch (err) {
            console.error('[AdminStickers] Failed to load pack stickers:', err);
            setPackStickers([]);
        } finally {
            setPackLoading(false);
        }
    };

    // ====================================================================
    //  Добавление стикеров в пак
    // ====================================================================

    const handleAddStickers = async () => {
        if (!selectedPack || addStickerFiles.length === 0) return;

        try {
            setAddStickerUploading(true);
            setError(null);

            // Загружаем файлы
            const batchRes = await filesApi.uploadBatch(addStickerFiles, { entityType: 'CHAT' });
            const batchFiles = batchRes?.files || [];
            const fileIds = batchFiles.map(f => f?.id).filter(Boolean);

            if (fileIds.length === 0) {
                throw new Error('Не удалось загрузить файлы стикеров');
            }

            // Добавляем стикеры в пак
            const newStickers = [];
            for (let i = 0; i < fileIds.length; i++) {
                const sticker = await stickerPacksApi.addSticker(selectedPack.id, {
                    imageFileId: fileIds[i],
                    sortOrder: packStickers.length + i,
                    isActive: true,
                });
                newStickers.push(sticker);
            }

            setPackStickers(prev => [...prev, ...newStickers]);
            setAddStickerFiles([]);
        } catch (err) {
            setError(`Не удалось добавить стикеры: ${err.message}`);
            console.error('[AdminStickers] Add stickers failed:', err);
        } finally {
            setAddStickerUploading(false);
        }
    };

    // ====================================================================
    //  Удаление стикера из пака
    // ====================================================================

    const handleDeleteSticker = async (stickerId) => {
        if (!selectedPack) return;
        if (!window.confirm('Удалить этот стикер из пака?')) return;

        try {
            await stickerPacksApi.deleteSticker(selectedPack.id, stickerId);
            setPackStickers(prev => prev.filter(s => s.id !== stickerId));
        } catch (err) {
            setError(`Не удалось удалить стикер: ${err.message}`);
            console.error('[AdminStickers] Delete sticker failed:', err);
        }
    };

    // ====================================================================
    //  Редактирование пака
    // ====================================================================

    const handleOpenEdit = () => {
        if (!selectedPack) return;
        setEditPackName(selectedPack.name || '');
        setShowEditModal(true);
    };

    const handleEditPack = async () => {
        if (!selectedPack || !editPackName.trim()) return;

        try {
            setActionLoading(true);
            await stickerPacksApi.updateStickerPack(selectedPack.id, { name: editPackName.trim() });
            setShowEditModal(false);
            // Обновляем в списке
            setPacks(prev => prev.map(p =>
                p.id === selectedPack.id ? { ...p, name: editPackName.trim() } : p
            ));
            setSelectedPack(prev => ({ ...prev, name: editPackName.trim() }));
        } catch (err) {
            setError(`Не удалось обновить пак: ${err.message}`);
            console.error('[AdminStickers] Edit pack failed:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // ====================================================================
    //  Удаление пака
    // ====================================================================

    const handleOpenDelete = (pack) => {
        setDeleteTarget(pack);
        setShowDeleteModal(true);
    };

    const handleDeletePack = async () => {
        if (!deleteTarget) return;

        try {
            setActionLoading(true);
            await stickerPacksApi.deleteStickerPack(deleteTarget.id);
            setShowDeleteModal(false);
            setDeleteTarget(null);
            if (selectedPack?.id === deleteTarget.id) {
                setShowPackModal(false);
                setSelectedPack(null);
            }
            await loadPacks();
        } catch (err) {
            setError(`Не удалось удалить пак: ${err.message}`);
            console.error('[AdminStickers] Delete pack failed:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // ====================================================================
    //  Вспомогательные функции
    // ====================================================================

    const getStickerImageUrl = (sticker) => {
        const fileId = sticker.imageFileId || sticker.fileId;
        if (!fileId) return null;
        if (typeof fileId === 'string' && fileId.startsWith('/')) return fileId;
        return `/api/files/${fileId}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('ru-RU');
    };

    // ====================================================================
    //  Рендер
    // ====================================================================

    if (loading && packs.length === 0) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">Управление стикерами</h1>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <HrnbCard
                title="Стикерпаки"
                actions={
                    <Button variant="primary" size="sm" onClick={handleOpenCreate}>
                        + Создать пак
                    </Button>
                }
            >
                <div className="admin-pagination-header">
                    <Form.Select
                        className="admin-page-size-selector"
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value={5}>5 на странице</option>
                        <option value={10}>10 на странице</option>
                        <option value={25}>25 на странице</option>
                        <option value={50}>50 на странице</option>
                    </Form.Select>
                </div>

                <Table responsive hover className="admin-stickers-table">
                    <thead>
                        <tr>
                            <th>Иконка</th>
                            <th>Название</th>
                            <th>Стикеров</th>
                            <th>Порядок</th>
                            <th>Статус</th>
                            <th>Дата создания</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {packs.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="text-center text-muted">
                                    Стикерпаков не найдено
                                </td>
                            </tr>
                        ) : (
                            packs.map((pack) => (
                                <tr key={pack.id}>
                                    <td>
                                        <span className="admin-stickers__pack-icon">
                                            {pack.icon || '📦'}
                                        </span>
                                    </td>
                                    <td>
                                        <strong>{pack.name}</strong>
                                        <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                            ID: {pack.id?.substring(0, 8)}...
                                        </div>
                                    </td>
                                    <td>{pack.stickers?.length || 0}</td>
                                    <td>{pack.sortOrder ?? 0}</td>
                                    <td>
                                        <Badge bg={pack.isActive ? 'success' : 'secondary'}>
                                            {pack.isActive ? 'Активен' : 'Неактивен'}
                                        </Badge>
                                    </td>
                                    <td>{formatDate(pack.createdAt)}</td>
                                    <td>
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            className="me-1"
                                            onClick={() => handleOpenPack(pack)}
                                        >
                                            Стикеры
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => handleOpenDelete(pack)}
                                            disabled={actionLoading}
                                        >
                                            Удалить
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* ================================================================
                Модальное окно: создание пака
            ================================================================ */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Создать стикерпак</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Название пака</Form.Label>
                            <Form.Control
                                type="text"
                                value={newPackName}
                                onChange={(e) => setNewPackName(e.target.value)}
                                placeholder="Например: Эмоции, Жесты..."
                                disabled={actionLoading}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Изображения стикеров</Form.Label>
                            <label className="admin-stickers__upload-zone">
                                <span className="admin-stickers__upload-zone-icon">📁</span>
                                <span>
                                    {newPackFiles.length > 0
                                        ? `Выбрано файлов: ${newPackFiles.length}`
                                        : 'Нажмите, чтобы выбрать SVG/PNG файлы'}
                                </span>
                                <input
                                    type="file"
                                    multiple
                                    accept="image/svg+xml,image/png,image/webp,image/gif"
                                    style={{ display: 'none' }}
                                    disabled={actionLoading}
                                    onChange={(e) => {
                                        const files = Array.from(e.target.files || []);
                                        setNewPackFiles(files);
                                    }}
                                />
                            </label>
                            {newPackFiles.length > 0 && (
                                <div className="admin-stickers__upload-preview">
                                    {newPackFiles.map((file, idx) => (
                                        <div key={idx} className="admin-stickers__upload-preview-item">
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={file.name}
                                            />
                                            <span className="preview-filename">{file.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>
                                Эмодзи для стикеров (через запятую, опционально)
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={newPackEmojis}
                                onChange={(e) => setNewPackEmojis(e.target.value)}
                                placeholder="😀,😢,😠,😆,😍"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                По одному эмодзи на стикер, в том же порядке, что и файлы
                            </Form.Text>
                        </Form.Group>

                        {createProgress && (
                            <div className="admin-stickers__progress">
                                <p className="small text-muted mb-1">
                                    {createProgress.step === 'upload-stickers' && 'Загрузка стикеров...'}
                                    {createProgress.step === 'create-pack' && 'Создание пака...'}
                                    {createProgress.step === 'add-stickers' && 'Добавление стикеров...'}
                                    {' '}({createProgress.completed}/{createProgress.total})
                                </p>
                                <div className="progress" style={{ height: '8px' }}>
                                    <div
                                        className="progress-bar"
                                        role="progressbar"
                                        style={{ width: `${(createProgress.completed / createProgress.total) * 100}%` }}
                                        aria-valuenow={createProgress.completed}
                                        aria-valuemin={0}
                                        aria-valuemax={createProgress.total}
                                    />
                                </div>
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={actionLoading}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={handleCreatePack} disabled={actionLoading || !newPackName.trim() || newPackFiles.length === 0}>
                        {actionLoading ? 'Создание...' : 'Создать пак'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ================================================================
                Модальное окно: просмотр пака со стикерами
            ================================================================ */}
            <Modal show={showPackModal} onHide={() => setShowPackModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Стикеры пака: {selectedPack?.name}
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="ms-2"
                            onClick={handleOpenEdit}
                        >
                            ✏️ Редактировать
                        </Button>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {packLoading ? (
                        <div className="text-center py-4">
                            <HrnbLoader />
                        </div>
                    ) : (
                        <>
                            {/* Список стикеров */}
                            <div className="admin-stickers__pack-detail-section">
                                <h6>Стикеры ({packStickers.length})</h6>
                                {packStickers.length === 0 ? (
                                    <p className="text-muted small">В этом паке пока нет стикеров</p>
                                ) : (
                                    <div className="admin-stickers__sticker-grid">
                                        {packStickers.map((sticker) => (
                                            <div key={sticker.id} className="admin-stickers__sticker-item">
                                                <button
                                                    className="sticker-delete-btn"
                                                    onClick={() => handleDeleteSticker(sticker.id)}
                                                    title="Удалить стикер"
                                                >
                                                    ✕
                                                </button>
                                                <img
                                                    src={getStickerImageUrl(sticker)}
                                                    alt={sticker.emoji || 'Стикер'}
                                                    loading="lazy"
                                                />
                                                {sticker.emoji && (
                                                    <span className="sticker-emoji">{sticker.emoji}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <hr />

                            {/* Добавление стикеров */}
                            <div className="admin-stickers__pack-detail-section">
                                <h6>Добавить стикеры</h6>
                                <label className="admin-stickers__upload-zone">
                                    <span className="admin-stickers__upload-zone-icon">➕</span>
                                    <span>
                                        {addStickerFiles.length > 0
                                            ? `Выбрано файлов: ${addStickerFiles.length}`
                                            : 'Нажмите, чтобы выбрать файлы стикеров'}
                                    </span>
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/svg+xml,image/png,image/webp,image/gif"
                                        style={{ display: 'none' }}
                                        disabled={addStickerUploading}
                                        onChange={(e) => {
                                            const files = Array.from(e.target.files || []);
                                            setAddStickerFiles(files);
                                        }}
                                    />
                                </label>
                                {addStickerFiles.length > 0 && (
                                    <div className="admin-stickers__upload-preview">
                                        {addStickerFiles.map((file, idx) => (
                                            <div key={idx} className="admin-stickers__upload-preview-item">
                                                <img
                                                    src={URL.createObjectURL(file)}
                                                    alt={file.name}
                                                />
                                                <span className="preview-filename">{file.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {addStickerFiles.length > 0 && (
                                    <div className="mt-2">
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={handleAddStickers}
                                            disabled={addStickerUploading}
                                        >
                                            {addStickerUploading ? 'Загрузка...' : `Загрузить ${addStickerFiles.length} стикер(ов)`}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            {/* ================================================================
                Модальное окно: редактирование пака
            ================================================================ */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Редактировать пак</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Название пака</Form.Label>
                            <Form.Control
                                type="text"
                                value={editPackName}
                                onChange={(e) => setEditPackName(e.target.value)}
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={actionLoading}>
                        Отмена
                    </Button>
                    <Button variant="primary" onClick={handleEditPack} disabled={actionLoading || !editPackName.trim()}>
                        {actionLoading ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ================================================================
                Модальное окно: подтверждение удаления пака
            ================================================================ */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Удаление стикерпака</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Вы уверены, что хотите удалить стикерпак <strong>{deleteTarget?.name}</strong>?</p>
                    <p className="text-danger small mb-0">
                        Все стикеры внутри пака также будут удалены. Это действие необратимо.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={actionLoading}>
                        Отмена
                    </Button>
                    <Button variant="danger" onClick={handleDeletePack} disabled={actionLoading}>
                        {actionLoading ? 'Удаление...' : 'Удалить'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminStickers;